<template>
  <FormSection
    id="spend-steering-form"
    :icon="IconColoredBoatWheel"
    :title="capitalize(i18n.t('names.spendSteering'))"
    :can-be-toggled="true"
    :is-toggled="isSpendSteeringEnabled"
    :is-toggle-disabled="!canToggleSteering"
    :toggle-tool-tip-message="toolTipMessage"
    @toggled="toggleSpendSteering">
    <!-- Info tooltip / Beta label -->
    <template #pane-header-right>
      <span class="badge badge-info badge-xs ml-3">Beta</span>
      <div class="col-sm-1 field-help">
        <div v-tooltip="capitalizeTranslation('messages.dealSpendSteeringInfo')">
          <icon-info />
        </div>
      </div>
    </template>
    <!-- Dimension Type -->
    <div>
      <form-separator> {{ capitalizeTranslation('names.dimensionType') }}</form-separator>
      <div class="configuration-tiles d-flex justify-content-center">
        <selectable-tiles
          class="dimension-type"
          :items="steeringTypeOptions"
          :selected-items="steeringTypeOptions.filter((option) => option.id === dealSpendSteering.dimensionType)"
          :should-display-labels="true"
          :should-display-icons="true"
          :is-multiselect="false"
          :allow-empty="false"
          :should-display-checkmark-on-selected-items="true"
          tile-padding="1rem 1rem 1.5rem"
          label-property-name="name"
          icon-property-name="icon"
          is-disabled-property-name="disabled"
          disabled-message-property-name="disabledMessage"
          @selection-changed="onSteeringDimensionTypeChanged" />
      </div>
    </div>
    <!-- Budget Mode -->
    <div v-if="dealSpendSteeringDimensionType">
      <form-separator> {{ capitalize(i18n.t('names.budgetMode')) }}</form-separator>
      <div class="configuration-tiles d-flex justify-content-center">
        <selectable-tiles
          class="budget-mode"
          :items="steeringBudgetModeOptions"
          :selected-items="steeringBudgetModeOptions.filter((option) => option.id === dealSpendSteering.budgetMode)"
          :is-multiselect="false"
          :allow-empty="false"
          :should-display-labels="true"
          :should-display-checkmark-on-selected-items="true"
          label-property-name="name"
          tile-padding="1.5rem"
          @selection-changed="onSteeringBudgetModeChanged" />
      </div>
    </div>
    <!-- Steering Dimensions Table -->
    <div
      v-if="dealSpendSteeringDimensionType"
      class="deal-dimension">
      <form-separator> {{ capitalizeTranslation('names.steeringDimensions') }}</form-separator>
      <deal-spend-steering-table
        :suppliers="suppliers"
        :active-deal="activeDeal"
        @selected-dimensions-changed="(dimensions) => (selectedDimensions = dimensions)" />
    </div>
  </FormSection>
</template>

<script setup lang="ts">
  import { capitalize, computed, markRaw, onBeforeMount, Ref, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';

  import FormSection from '@/common/components/controls/FormSection.vue';
  import FormSeparator from '@/common/components/controls/FormSeparator.vue';
  import SelectableTiles from '@/common/components/controls/SelectableTiles.vue';
  import DealSpendSteeringTable from '@/common/components/deals/form/spendSteering/DealSpendSteeringTable.vue';
  import BudgetModes from '@/common/enums/budget-modes.enum';
  import { IconColoredBoatWheel, IconColoredDeal, IconColoredMediaOwner } from '@/common/icons/colored';
  import { IconInfo } from '@/common/icons/monochrome';
  import { Package } from '@/common/models';
  import MediaOwner from '@/common/models/media-owner/MediaOwner';
  import { IOption } from '@/common/models/Option';
  import SpendSteering, {
    ISpendSteeringDimension,
    SelectableDimensionType,
  } from '@/common/models/package/SpendSteering';
  import { areArraysEqual, capitalizeTranslation, useInjected } from '@/globals';
  import { useDealComputedProperties } from '@/org-app/services/computedProperties/DealComputedProperties';
  import { waitUntil } from '@/utils/DelayHelper';
  import { mapActions } from '@/utils/StateMapping';

  //#region Data

  // Props
  interface IProps {
    suppliers: MediaOwner[];
    activeDeal?: Package;
  }

  const { suppliers, activeDeal = null } = defineProps<IProps>();

  const deal: Ref<Package> = useInjected<Package>('deal');

  // State
  const { updatePackageSpendSteering } = mapActions();

  // Refs
  const selectedDimensions = ref([]);

  const i18n = useI18n();

  //#endregion

  //#region Computed

  const { dealSpendSteering, dealSpendSteeringDimensionType } = useDealComputedProperties(deal);

  const isSpendSteeringEnabled = computed(() => {
    return dealSpendSteering.value !== null;
  });

  const dealSpendSteeringDimensions = computed({
    get() {
      return dealSpendSteering.value?.spendSteeringDimensions || [];
    },
    set(value: ISpendSteeringDimension[]) {
      // To maintain reactivity, we need to clone the object and assign it back to the deal.
      const updatedDealSpendSteering = dealSpendSteering.value.clone();
      updatedDealSpendSteering.spendSteeringDimensions = value;
      handleWildCard(updatedDealSpendSteering);
      updatePackageSpendSteering({
        package: deal.value,
        value: updatedDealSpendSteering,
      });
    },
  });

  const dealSpendSteeringBudgetMode = computed({
    get() {
      return dealSpendSteering.value?.budgetMode;
    },
    set(value: BudgetModes) {
      const updatedDealSpendSteering = dealSpendSteering.value.clone();
      updatedDealSpendSteering.budgetMode = value;
      updatePackageSpendSteering({
        package: deal.value,
        value: updatedDealSpendSteering,
      });
    },
  });

  const steeringTypeOptions = computed(() => {
    return [
      { id: SelectableDimensionType.DEAL, name: capitalizeTranslation('names.deal'), icon: markRaw(IconColoredDeal) },
      {
        id: SelectableDimensionType.SELLER,
        name: capitalizeTranslation('names.mediaOwner'),
        icon: markRaw(IconColoredMediaOwner),
        disabled: availablePublishers.value.length <= 1,
        disabledMessage: capitalizeTranslation('messages.spendSteeringMinimumPublishers'),
      },
    ];
  });

  const steeringBudgetModeOptions = computed(() => {
    return [
      { id: BudgetModes.BUDGET, name: capitalizeTranslation('names.budget') },
      { id: BudgetModes.IMPRESSIONS, name: capitalizeTranslation('validatorNames.impressions') },
    ];
  });

  const toolTipMessage = computed(() => {
    return !canToggleSteering.value ? capitalizeTranslation('messages.spendSteeringMinimumPackagesSelected') : null;
  });

  const canToggleSteering = computed(() => {
    return deal.value.packages?.length > 1;
  });

  const availablePublishers = computed(() => {
    return deal.value.mediaOwnerIDs.length ? [...new Set(deal.value.mediaOwnerIDs.map(String))] : [];
  });

  const availableDimensions = computed(() => {
    return dealSpendSteeringDimensionType.value === SelectableDimensionType.DEAL
      ? deal.value.packages.map((option: Package) => option.dealID)
      : availablePublishers.value;
  });

  //#endregion

  //#region Methods

  function toggleSpendSteering(enabled: boolean = true) {
    if (dealSpendSteering.value != null && enabled) return;
    dealSpendSteering.value = enabled ? new SpendSteering() : null;
  }

  function onSteeringDimensionTypeChanged(selectedTypes: IOption[]) {
    // Since we only allow for single selection, we can safely assume that the first item is selected.
    if (selectedTypes.length === 0) return;
    const selectedDimension = selectedTypes[0];
    // If spend steering is not enabled, we should enable it to instantiate the model.
    if (!isSpendSteeringEnabled.value) toggleSpendSteering();
    // If the selected dimension is the same as the current one, there's no point of updating the deal.
    if (selectedDimension.id === dealSpendSteering.value.dimensionType) return;
    dealSpendSteeringDimensionType.value = selectedDimension.id as SelectableDimensionType;
    selectedDimensions.value = [];
  }

  function onSteeringBudgetModeChanged(selectedBudgetModes: IOption[]) {
    if (selectedBudgetModes.length === 0) return;
    const selectedBudgetMode = selectedBudgetModes[0];
    if (!isSpendSteeringEnabled.value) toggleSpendSteering();
    if (selectedBudgetMode.id === dealSpendSteeringBudgetMode.value) return;
    dealSpendSteeringBudgetMode.value = selectedBudgetMode.id as BudgetModes;
  }

  function handleWildCard(updatedSteering: SpendSteering) {
    // Grab all the active dimensions that are not the wild card
    const activeDimensions = updatedSteering.spendSteeringDimensions
      .filter((d) => d.dimensionValue !== '*')
      .map((d) => d.dimensionValue);
    if (!activeDimensions.length) {
      updatedSteering.spendSteeringDimensions = [];
      return updatedSteering;
    }
    // Determine if there are any missing dimensions
    const remainingDimensions = availableDimensions.value.filter((d) => !activeDimensions.includes(d));
    const wildCardIndex = updatedSteering.spendSteeringDimensions.findIndex((d) => d.dimensionValue === '*');
    if (remainingDimensions.length && wildCardIndex === -1) {
      // If any dimensions are missing, we should add the wild card if it doesn't already exist.
      updatedSteering.spendSteeringDimensions.push({
        dimensionValue: '*',
        steeringPercentage: 0,
        highPriority: false,
      });
    } else if (!remainingDimensions.length && wildCardIndex !== -1) {
      // When all dimensions have been selected, we should remove the wild card if it exists.
      updatedSteering.spendSteeringDimensions.splice(wildCardIndex, 1);
    }
    return updatedSteering;
  }

  function updateSteeringDimensions() {
    // In the case where steering on publisher is no longer possible, we should default to deal
    if (
      dealSpendSteeringDimensionType.value === SelectableDimensionType.SELLER &&
      availablePublishers.value.length <= 1
    ) {
      dealSpendSteeringDimensionType.value = SelectableDimensionType.DEAL;
    }
    // Creates a set of the new selected dimension values based on the selected deals
    const selectedDimensionSet = new Set(selectedDimensions.value);
    const steeringDimensions = dealSpendSteeringDimensions.value.filter(
      (d: ISpendSteeringDimension) => d.dimensionValue !== '*',
    );
    // Creates a set of the existing dimension values
    const existingDimensionValues = new Set(steeringDimensions.map((d: ISpendSteeringDimension) => d.dimensionValue));
    // Removes dimensions that are no longer selected
    const updatedDimensions = steeringDimensions.filter((d: ISpendSteeringDimension) => {
      return selectedDimensionSet.has(d.dimensionValue);
    });
    // Adds new dimensions that aren't already included
    const missingDimensions = selectedDimensionSet.difference(existingDimensionValues) as Set<string>;
    missingDimensions.forEach((dimensionValue: string) => {
      updatedDimensions.push({
        dimensionValue,
        steeringPercentage: 0,
        highPriority: false,
      });
    });
    dealSpendSteeringDimensions.value = updatedDimensions;
  }

  //#endregion

  //#region Watchers

  watch(selectedDimensions, (newDimensions, oldDimensions) => {
    if (
      areArraysEqual(newDimensions, oldDimensions) ||
      !isSpendSteeringEnabled.value ||
      !dealSpendSteeringDimensionType.value
    )
      return;
    updateSteeringDimensions();
  });

  watch(
    () => deal.value.packages,
    async (newOptions, oldOptions) => {
      // Early exit conditions
      if (
        !isSpendSteeringEnabled.value ||
        !dealSpendSteeringDimensionType.value ||
        newOptions.length === oldOptions.length
      )
        return;
      // If all packages have been removed, we need to disable spend steering
      if (newOptions.length <= 1) {
        toggleSpendSteering(false);
        return;
      }
      const hasNewPackages = newOptions.length > oldOptions.length;
      if (
        hasNewPackages &&
        newOptions.length > dealSpendSteeringDimensions.value.length &&
        dealSpendSteeringDimensions.value.findIndex((d) => d.dimensionValue === '*') === -1
      ) {
        // Trigger an update so that the wild card is added....

        dealSpendSteeringDimensions.value = dealSpendSteeringDimensions.value;
        return;
      }
      if (hasNewPackages) return;
      const availableDimensionsSet = new Set([
        ...(dealSpendSteeringDimensionType.value === SelectableDimensionType.DEAL
          ? newOptions.map((option: Package) => option.dealID)
          : newOptions.map((option: Package) => String(option.organizationID))),
      ]);
      // When steering on publisher is no longer possible, we should switch to deal
      if (dealSpendSteeringDimensionType.value === SelectableDimensionType.SELLER && availableDimensionsSet.size <= 1) {
        dealSpendSteeringDimensionType.value = SelectableDimensionType.DEAL;
        return;
      }
      const selectedDimensionsSet = new Set(selectedDimensions.value);
      const removedDimensions = selectedDimensionsSet.difference(availableDimensionsSet);
      // If nothing was removed we don't need to update the selected dimensions
      if (!removedDimensions.size) return;
      const updatedDimensions = selectedDimensionsSet.difference(removedDimensions);
      selectedDimensions.value = Array.from(updatedDimensions);
    },
  );

  //#endregion

  onBeforeMount(async () => {
    await waitUntil(() => deal.value !== null);
    if (!dealSpendSteeringDimensions.value.length) return;
    selectedDimensions.value = dealSpendSteeringDimensions.value.map((d) => d.dimensionValue);
  });
</script>

<style scoped lang="scss">
  @use '@/styles/variables' as *;

  .configuration-tiles {
    padding: 0 1.6rem;
  }

  .budget-mode {
    :deep(.selectable-tile) {
      padding: 2rem;
    }
  }
</style>
